import { useEffect, useRef, useState } from 'react';
import DrmMetricsService from '../services/drm-metrics-service';
import { hasSyncedLastRefreshDate } from '../util/organisation';
import { useCacheActions } from './useFetch';
import { ORG_SYNC_COMPLETED_EVENT } from './useOrganisationSync';

const POLL_INTERVAL_MS = 8_000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const FRESHNESS_DELAY_MS = 500;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function normalizeRefreshDate(value) {
  if (!hasSyncedLastRefreshDate(value)) return null;
  return new Date(value).toISOString();
}

function useDrmMetricsSyncPoll({ shortCode, lastRefreshDate }) {
  const { invalidateDomain } = useCacheActions();
  const invalidateDomainRef = useRef(invalidateDomain);
  const baselineRef = useRef(normalizeRefreshDate(lastRefreshDate));
  const lastRefreshDateRef = useRef(lastRefreshDate);
  const invalidatedForRefreshRef = useRef(null);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const pollingRef = useRef(false);
  const [isRecalculating, setIsRecalculating] = useState(false);

  invalidateDomainRef.current = invalidateDomain;
  lastRefreshDateRef.current = lastRefreshDate;

  useEffect(() => {
    if (pollingRef.current) return;
    baselineRef.current = normalizeRefreshDate(lastRefreshDate);
  }, [lastRefreshDate]);

  useEffect(() => {
    if (!shortCode) return undefined;

    function stopPolling() {
      pollingRef.current = false;
      setIsRecalculating(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    async function pollOnce() {
      if (!pollingRef.current || !shortCode) return;

      try {
        await wait(FRESHNESS_DELAY_MS);
        if (!pollingRef.current) return;

        const freshness = await DrmMetricsService.getFreshness({
          shortCode,
          clientLastRefreshDate: baselineRef.current,
        });

        const orgRefresh = normalizeRefreshDate(freshness.lastRefreshDate);
        const baseline = baselineRef.current;

        if (
          !freshness.syncInProgress &&
          orgRefresh &&
          orgRefresh !== baseline &&
          invalidatedForRefreshRef.current !== orgRefresh
        ) {
          invalidatedForRefreshRef.current = orgRefresh;
          invalidateDomainRef.current('drmMetrics');
          invalidateDomainRef.current('organisation');
          baselineRef.current = orgRefresh;
        }

        if (freshness.upToDate) {
          stopPolling();
        }
      } catch {
        // Keep polling until timeout; transient network errors should not abort.
      }
    }

    function startPolling(eventShortCode) {
      if (eventShortCode && eventShortCode !== shortCode) return;

      baselineRef.current = normalizeRefreshDate(lastRefreshDateRef.current);
      invalidatedForRefreshRef.current = null;
      stopPolling();
      pollingRef.current = true;
      setIsRecalculating(true);

      pollOnce();
      intervalRef.current = setInterval(pollOnce, POLL_INTERVAL_MS);
      timeoutRef.current = setTimeout(stopPolling, POLL_TIMEOUT_MS);
    }

    function onSyncCompleted(event) {
      startPolling(event?.detail?.shortCode);
    }

    let cancelled = false;

    async function bootstrapFreshnessCheck() {
      try {
        const freshness = await DrmMetricsService.getFreshness({
          shortCode,
          clientLastRefreshDate: baselineRef.current,
        });

        if (cancelled) return;

        if (freshness.syncInProgress) {
          startPolling();
        }
      } catch {
        // Leave the default one
      }
    }

    bootstrapFreshnessCheck();

    window.addEventListener(ORG_SYNC_COMPLETED_EVENT, onSyncCompleted);

    return () => {
      cancelled = true;
      window.removeEventListener(ORG_SYNC_COMPLETED_EVENT, onSyncCompleted);
      stopPolling();
    };
  }, [shortCode]);

  return { isRecalculating };
}

export default useDrmMetricsSyncPoll;
